import type { SceneData, ChoiceCondition, SceneConfig } from '../scenes/types'
import { StoryScene } from '../../components/StoryScene'
import type { SaveState } from '../save-state'
import { SaveStateManager } from '../save-state'
import { debugLog } from '../debug'
import {
  triggerPreloadAhead,
  preloadInitialScenes as doPreloadInitialScenes,
  resetPreloadState,
  type PreloadContext,
  type AudioPreloadConfig,
} from './preload'
import { restoreState as restoreStateFn } from './state-restoration'
import {
  evaluateChoiceCondition as evaluateChoiceConditionFn,
  resolveChoiceResultForRestore as resolveChoiceResultForRestoreFn,
  shouldLoadChoiceScene as shouldLoadChoiceSceneFn,
} from './choice-restore'
import track, { UNLOCK_SCENE } from '../analytics'
import {
  loadGameStateToScene as loadGameStateToSceneFn,
  scrollToScene as scrollToSceneFn,
  clearContainerExceptIntro,
} from './debug-scene'
import {
  getNextLinearSceneIndex,
  buildSceneConfig,
  appendSpacerIfLastIsScene,
  ensureSpacerAfter,
  moveWrapperAfter,
} from './scene-loading-helpers'

/** Delay (ms) before showing scene after append/spacer (allows layout). */
const DELAY_AFTER_LOAD_MS = 100

export interface StoryEngineConfig extends SceneConfig {
  chapterTitle: { margin: number; scrollDistance: number; stickAt: number }
  scrollSpeed: {
    base: number
    baseMobile: number
    inScene: number
    inSceneMobile: number
    fromTop: number
    fromBottom: number
  }
}

/**
 * Story engine: orchestrates scene loading, preload, save/restore, and choice flow.
 * Main flow: start() / reset() → loadScene() / loadSceneById() / loadSceneAtPosition(); delegates to preload, state-restoration, choice-restore, debug-scene, scene-loading-helpers.
 */
export class StoryEngine {
  private scenes: SceneData[] = []
  private loadedScenes: Map<string, StoryScene> = new Map() // Use string IDs (scene.id)
  private container: HTMLElement
  private flags: Record<string, boolean> = {}
  private currentSceneId: string | null = null
  private onStateChangeCallback?: () => void
  private onStoryEndCallback?: () => void
  private onBeforeResetCallback?: () => void
  private onEpilogueSceneCallback?: (sceneId: string, element: string, container: HTMLElement) => void
  private storyEndFired = false
  private isRestoringState = false
  private loadedEpilogueScenes: Set<string> = new Set()
  private preloadedScenes: Set<string> = new Set()
  private config: StoryEngineConfig
  private audioPreloadConfig?: AudioPreloadConfig
  private loadGeneration = 0

  constructor(container: HTMLElement, scenes: SceneData[], config: StoryEngineConfig) {
    this.container = container
    this.scenes = scenes
    this.config = config
  }

  setOnStateChange(callback: () => void): void {
    this.onStateChangeCallback = callback
  }

  setOnStoryEnd(callback: () => void): void {
    this.onStoryEndCallback = callback
  }

  setOnBeforeReset(callback: () => void): void {
    this.onBeforeResetCallback = callback
  }

  setOnEpilogueScene(callback: (sceneId: string, element: string, container: HTMLElement) => void): void {
    this.onEpilogueSceneCallback = callback
  }

  private fireStoryEnd(): void {
    if (this.storyEndFired || !this.onStoryEndCallback) return
    this.storyEndFired = true
    this.onStoryEndCallback()
  }

  /** After state restoration, check whether the last loaded scene is the final one. */
  private checkAndFireStoryEnd(): void {
    const { lastLoadedSceneIndex } = this.computeLastLoadedIndexAndBubble()
    if (lastLoadedSceneIndex < 0) return
    const nextIndex = getNextLinearSceneIndex(this.scenes, lastLoadedSceneIndex, (c) =>
      this.evaluateChoiceCondition(c),
    )
    if (nextIndex === null) {
      this.fireStoryEnd()
    }
  }

  private notifyStateChange(): void {
    if (this.onStateChangeCallback) {
      this.onStateChangeCallback()
    }
  }

  /**
   * Completely reset the story engine - clear all loaded scenes, container, and state
   */
  reset(): void {
    this.onBeforeResetCallback?.()
    this.loadGeneration++
    this.storyEndFired = false

    // Destroy all loaded scenes first
    this.loadedScenes.forEach((scene) => {
      try {
        scene.destroy()
      } catch (error) {
        console.warn('Error destroying scene:', error)
      }
    })
    this.loadedScenes.clear()

    this.loadedEpilogueScenes.clear()
    resetPreloadState()

    // Reset state
    this.currentSceneId = null
    this.flags = {}
    this.isRestoringState = false

    clearContainerExceptIntro(this.container)
  }

  start(loadState?: SaveState, onContentReady?: () => void): void {
    if (this.scenes.length === 0) {
      console.error('No scenes available')
      return
    }
    this.reset()

    if (loadState) {
      this.restoreState(loadState, () => {
        this.catchUpAfterRestoredCompletedMinigames()
        this.checkAndFireStoryEnd()
        onContentReady?.()
      })
    } else {
      this.loadScene(0)
      if (onContentReady) {
        this.whenContainerImagesReady(onContentReady)
      }
    }
  }

  /** Run callback once all img elements in the container have loaded (or there are none), with a max timeout. */
  private whenContainerImagesReady(onReady: () => void): void {
    const MAX_WAIT_MS = 3000
    const gen = this.loadGeneration
    requestAnimationFrame(() => {
      if (gen !== this.loadGeneration) return
      setTimeout(() => {
        if (gen !== this.loadGeneration) return
        const images = this.container.querySelectorAll('img')
        const total = images.length
        if (total === 0) {
          onReady()
          return
        }
        let fired = false
        const fire = () => {
          if (fired || gen !== this.loadGeneration) return
          fired = true
          requestAnimationFrame(() => requestAnimationFrame(onReady))
        }
        let loaded = 0
        const check = () => {
          loaded++
          if (loaded === total) fire()
        }
        images.forEach((img) => {
          if ((img as HTMLImageElement).complete) check()
          else {
            img.addEventListener('load', check, { once: true })
            img.addEventListener('error', check, { once: true })
          }
        })
        setTimeout(fire, MAX_WAIT_MS)
      }, 100)
    })
  }

  private restoreState(state: SaveState, onComplete?: () => void): void {
    restoreStateFn(
      state,
      {
        container: this.container,
        scenes: this.scenes,
        loadedScenes: this.loadedScenes,
        loadScene: (i, a) => this.loadScene(i, a),
        shouldLoadChoiceScene: (id, choices) => this.shouldLoadChoiceScene(id, choices),
        evaluateCondition: (c) => this.evaluateChoiceCondition(c),
        setRestoringFlag: (v) => {
          this.isRestoringState = v
        },
      },
      onComplete,
    )
  }

  // --- Scene loading (by index, by ID, at position) ---

  loadSceneAfterUnlock(sceneIndex: number): void {
    // Load scene after first scene is unlocked
    const sceneData = this.scenes[sceneIndex]
    if (sceneData && !this.loadedScenes.has(sceneData.id)) {
      this.loadScene(sceneIndex)
    }
  }

  /**
   * Load a scene by its ID (for choice results that lead to scenes)
   * If it's a choice result scene, insert it right after the choice scene that leads to it
   */
  loadSceneById(sceneId: string, animate: boolean = true, insertAfterSceneId?: string): void {
    const sceneIndex = this.scenes.findIndex((s) => s.id === sceneId)
    if (sceneIndex === -1) {
      console.error(`Scene with ID "${sceneId}" not found`)
      return
    }

    // If insertAfterSceneId is provided, insert the scene after that scene (for choice results)
    if (insertAfterSceneId) {
      const insertAfterScene = this.loadedScenes.get(insertAfterSceneId)
      if (insertAfterScene) {
        debugLog(`[StoryEngine] Loading choice result scene ${sceneId} after choice scene ${insertAfterSceneId}`)
        const insertAfterWrapper = insertAfterScene.getWrapper()
        if (insertAfterWrapper) {
          this.loadSceneAtPosition(sceneIndex, insertAfterWrapper, animate)
        } else {
          debugLog(`[StoryEngine] Could not get wrapper for scene ${insertAfterSceneId}`)
        }
        return
      } else {
        debugLog(`[StoryEngine] Choice scene ${insertAfterSceneId} not found in loaded scenes`)
      }
    }

    // Check if this is a choice result scene - if so, find the choice scene and insert after it
    const choiceSceneId = this.findChoiceSceneForResult(sceneId)
    if (choiceSceneId) {
      const choiceScene = this.loadedScenes.get(choiceSceneId)
      if (choiceScene) {
        // Load the scene but insert it after the choice scene instead of at the end
        const choiceWrapper = choiceScene.getWrapper()
        if (choiceWrapper) {
          this.loadSceneAtPosition(sceneIndex, choiceWrapper, animate)
        } else {
          debugLog(`[StoryEngine] Could not get wrapper for choice scene ${choiceSceneId}`)
        }
        return
      }
    }

    // Normal scene loading (append to end)
    this.loadScene(sceneIndex, animate)
  }

  /**
   * Find which scene has a choice that leads to the given scene ID
   */
  private findChoiceSceneForResult(resultSceneId: string): string | null {
    for (const scene of this.scenes) {
      if (!scene.choices) continue
      if (this.resolveChoiceResultForRestore(scene.choices.choice1.result, {}) === resultSceneId) return scene.id
      if (this.resolveChoiceResultForRestore(scene.choices.choice2.result, {}) === resultSceneId) return scene.id
    }
    return null
  }

  private getSceneConfig(): SceneConfig {
    return buildSceneConfig(this.config)
  }

  /** Create scene, attach all callbacks, render. Caller handles show/place/register. */
  private createAndPrepareScene(sceneData: SceneData, sceneIndex: number, insertAfterSceneId?: string): StoryScene {
    const scene = new StoryScene(sceneData, this.container, this.getSceneConfig(), sceneIndex)
    this.attachDebugClickHandler(scene)
    this.attachLinearProgressCallbacks(scene, sceneData, sceneIndex)
    this.attachCommonSceneCallbacks(scene, sceneData, insertAfterSceneId)
    scene.render()
    return scene
  }

  private attachDebugClickHandler(scene: StoryScene): void {
    scene.setOnSceneDebugClick((index) => {
      this.loadGameStateToScene(index)
      // No scrolling - user wants to stay at current scroll position
    })
  }

  /** Attach onComplete / onUnlock so the next linear scene loads when appropriate. */
  private attachLinearProgressCallbacks(scene: StoryScene, sceneData: SceneData, sceneIndex: number): void {
    if (sceneData.choices) return

    const gen = this.loadGeneration
    const loadNext = () => {
      if (gen !== this.loadGeneration) return
      if (this.isRestoringState) return
      const nextIndex = getNextLinearSceneIndex(this.scenes, sceneIndex, (c) => this.evaluateChoiceCondition(c))
      if (nextIndex !== null) {
        this.loadScene(nextIndex)
      } else {
        this.fireStoryEnd()
      }
    }

    if (sceneData.type === 'minigame') {
      scene.setOnComplete(loadNext)
      return
    }

    scene.setOnComplete(loadNext)
    const hasBubbles = sceneData.speechBubbles && sceneData.speechBubbles.length > 0
    if (hasBubbles) {
      scene.setOnUnlock(loadNext)
    } else {
      scene.setOnAfterShow(loadNext)
    }
  }

  /** Attach save-state and choice callbacks (bubble shown, action point, load scene by ID, evaluate condition). */
  private attachCommonSceneCallbacks(scene: StoryScene, sceneData: SceneData, insertAfterSceneId?: string): void {
    scene.setOnBubbleShown(() => this.saveCurrentState())
    scene.setOnActionPointClicked(() => this.saveCurrentState())
    scene.setOnLoadScene((sceneId) => {
      this.loadSceneById(sceneId, true, insertAfterSceneId)
    })
    scene.setOnEvaluateCondition((condition) => this.evaluateChoiceCondition(condition))
  }

  private registerLoadedScene(sceneData: SceneData, scene: StoryScene): void {
    this.loadedScenes.set(sceneData.id, scene)
    this.currentSceneId = sceneData.id
    this.saveCurrentState()
  }

  /**
   * Load a scene and insert it at a specific position in the DOM (after the reference element).
   * Flow: createAndPrepareScene → show → ensureSpacerAfter + moveWrapperAfter → register.
   */
  private loadSceneAtPosition(sceneIndex: number, insertAfterElement: HTMLElement, animate: boolean = true): void {
    if (sceneIndex < 0 || sceneIndex >= this.scenes.length) {
      console.error(`Scene index ${sceneIndex} out of range`)
      return
    }

    const sceneData = this.scenes[sceneIndex]
    if (!sceneData || this.loadedScenes.has(sceneData.id)) {
      if (!sceneData) console.error(`Scene at index ${sceneIndex} not found`)
      return
    }

    const scene = this.createAndPrepareScene(sceneData, sceneIndex)
    scene.show(animate)
    this.triggerPreload(sceneIndex)

    const sceneWrapper = scene.getWrapper()
    if (!sceneWrapper) {
      console.error(`[StoryEngine] Scene wrapper not found for ${sceneData.id}`)
      return
    }

    const { insertAfter, afterInsertPoint } = ensureSpacerAfter(this.container, insertAfterElement)
    moveWrapperAfter(this.container, sceneWrapper, insertAfter, afterInsertPoint)

    this.registerLoadedScene(sceneData, scene)
  }

  getScrollSpeedConfig(): {
    base: number
    baseMobile: number
    inScene: number
    inSceneMobile: number
    fromTop: number
    fromBottom: number
  } {
    return this.config.scrollSpeed
  }

  getAllSceneContainers(): HTMLElement[] {
    const panels = Array.from(this.container.querySelectorAll('.scene')) as HTMLElement[]
    const chapterTitles = Array.from(this.container.querySelectorAll('.chapter-title-scene')) as HTMLElement[]
    const minigames = Array.from(this.container.querySelectorAll('.minigame-scene')) as HTMLElement[]
    return [...panels, ...chapterTitles, ...minigames]
  }

  /**
   * Load a scene (append to container).
   * Flow: createAndPrepareScene → triggerPreload → appendSpacerIfLastIsScene + show (with optional rAF/delay) → register.
   */
  loadScene(sceneIndex: number, animate: boolean = true): void {
    if (sceneIndex < 0 || sceneIndex >= this.scenes.length) {
      console.error(`Scene index ${sceneIndex} out of range`)
      return
    }

    const sceneData = this.scenes[sceneIndex]
    if (!sceneData || this.loadedScenes.has(sceneData.id)) {
      if (!sceneData) console.error(`Scene at index ${sceneIndex} not found`)
      return
    }

    if (sceneData.type === 'epilogue') {
      this.loadEpilogueScene(sceneIndex, sceneData)
      return
    }

    const scene = this.createAndPrepareScene(sceneData, sceneIndex, sceneData.id)
    this.triggerPreload(sceneIndex)

    if (sceneIndex > 0 && animate) {
      const gen = this.loadGeneration
      requestAnimationFrame(() => {
        if (gen !== this.loadGeneration) return
        setTimeout(() => {
          if (gen !== this.loadGeneration) return
          appendSpacerIfLastIsScene(this.container)
          scene.show()
        }, DELAY_AFTER_LOAD_MS)
      })
    } else {
      if (sceneIndex > 0) appendSpacerIfLastIsScene(this.container)
      scene.show(animate)
    }

    this.registerLoadedScene(sceneData, scene)

    if (!this.isRestoringState) {
      track(UNLOCK_SCENE, sceneData.id)
    }
  }

  private loadEpilogueScene(sceneIndex: number, sceneData: SceneData): void {
    if (this.loadedEpilogueScenes.has(sceneData.id)) return

    if (sceneData.id === 'contest' && window.isContestRemoved) {
      this.loadedEpilogueScenes.add(sceneData.id)
      return
    }

    this.loadedEpilogueScenes.add(sceneData.id)

    const elementSelector = sceneData.element
    if (!elementSelector) {
      console.error(`[StoryEngine] Epilogue scene "${sceneData.id}" has no element selector`)
      return
    }

    if (sceneIndex > 0) appendSpacerIfLastIsScene(this.container)

    if (this.onEpilogueSceneCallback) {
      this.onEpilogueSceneCallback(sceneData.id, elementSelector, this.container)
    }

    this.currentSceneId = sceneData.id
    this.saveCurrentState()

    const gen = this.loadGeneration
    const loadNext = () => {
      if (gen !== this.loadGeneration) return
      if (this.isRestoringState) return
      const nextIndex = getNextLinearSceneIndex(this.scenes, sceneIndex, (c) => this.evaluateChoiceCondition(c))
      if (nextIndex !== null) {
        this.loadScene(nextIndex)
      } else {
        this.fireStoryEnd()
      }
    }

    requestAnimationFrame(() => {
      if (gen !== this.loadGeneration) return
      setTimeout(loadNext, DELAY_AFTER_LOAD_MS)
    })
  }

  // --- Save state ---

  /** Build the state to persist from current loaded scenes and scroll. */
  private computeSaveStatePayload(lenisScrollY?: number): SaveState {
    const scrollY = lenisScrollY !== undefined ? lenisScrollY : window.scrollY
    const { lastLoadedSceneIndex, lastSceneWithBubble } = this.computeLastLoadedIndexAndBubble()
    const existingState = SaveStateManager.load() || SaveStateManager.createInitialState()
    const sceneIdForBubble =
      lastSceneWithBubble?.sceneId ?? (lastLoadedSceneIndex >= 0 ? (this.scenes[lastLoadedSceneIndex]?.id ?? '') : '')

    return {
      scrollPosition: scrollY,
      lastLoadedScene: {
        index: lastLoadedSceneIndex,
        id: sceneIdForBubble,
        bubble: lastSceneWithBubble?.bubble,
      },
      choices: existingState.choices ?? {},
      minigames: existingState.minigames ?? {},
      inventory: existingState.inventory ?? [],
      consumedInventory: existingState.consumedInventory,
      contestSubmitted: existingState.contestSubmitted,
      clickReveals: existingState.clickReveals,
    }
  }

  private computeLastLoadedIndexAndBubble(): {
    lastLoadedSceneIndex: number
    lastSceneWithBubble: { sceneId: string; sceneIndex: number; bubble: number } | null
  } {
    let lastLoadedSceneIndex = -1
    let lastSceneWithBubble: { sceneId: string; sceneIndex: number; bubble: number } | null = null

    this.loadedScenes.forEach((scene, sceneId) => {
      const sceneIndex = this.scenes.findIndex((s) => s.id === sceneId)
      if (sceneIndex === -1) return
      if (sceneIndex > lastLoadedSceneIndex) lastLoadedSceneIndex = sceneIndex
      const lastBubble = scene.getLastUnlockedBubble()
      if (lastBubble !== null && (!lastSceneWithBubble || sceneIndex > lastSceneWithBubble.sceneIndex)) {
        lastSceneWithBubble = { sceneId, sceneIndex, bubble: lastBubble }
      }
    })

    for (const epilogueId of this.loadedEpilogueScenes) {
      const sceneIndex = this.scenes.findIndex((s) => s.id === epilogueId)
      if (sceneIndex > lastLoadedSceneIndex) lastLoadedSceneIndex = sceneIndex
    }

    return { lastLoadedSceneIndex, lastSceneWithBubble }
  }

  saveCurrentState(lenisScrollY?: number): void {
    if (this.isRestoringState) return
    SaveStateManager.save(this.computeSaveStatePayload(lenisScrollY))
    this.notifyStateChange()
  }

  /** Same as saveCurrentState but synchronous — use when leaving the game so progress is not lost to idle debounce. */
  saveCurrentStateImmediate(lenisScrollY?: number): void {
    if (this.isRestoringState) return
    SaveStateManager.saveImmediate(this.computeSaveStatePayload(lenisScrollY))
    this.notifyStateChange()
  }

  /**
   * While restoring, minigames may call onComplete before loadNext is allowed (isRestoringState).
   * If save still ends at a completed minigame, append the following linear scene(s).
   */
  private catchUpAfterRestoredCompletedMinigames(): void {
    if (this.isRestoringState) return
    const maxChain = 12
    for (let n = 0; n < maxChain; n++) {
      const { lastLoadedSceneIndex } = this.computeLastLoadedIndexAndBubble()
      if (lastLoadedSceneIndex < 0) return
      const sceneData = this.scenes[lastLoadedSceneIndex]
      if (!sceneData || sceneData.type !== 'minigame') return
      const saved = SaveStateManager.load()
      const mg = saved?.minigames?.[sceneData.id] as { completed?: boolean } | undefined
      if (!mg?.completed) return
      const nextIndex = getNextLinearSceneIndex(this.scenes, lastLoadedSceneIndex, (c) =>
        this.evaluateChoiceCondition(c),
      )
      if (nextIndex === null) return
      const nextData = this.scenes[nextIndex]
      if (!nextData || this.loadedScenes.has(nextData.id)) return
      this.loadScene(nextIndex, false)
    }
  }

  getLoadedSceneIds(): string[] {
    return Array.from(this.loadedScenes.keys())
  }

  getScenes(): SceneData[] {
    return this.scenes
  }

  // --- Debug (load state / scroll to scene) ---

  loadGameStateToScene(targetSceneIndex: number): void {
    this.onBeforeResetCallback?.()
    this.loadedEpilogueScenes.clear()
    this.isRestoringState = true
    loadGameStateToSceneFn(targetSceneIndex, {
      container: this.container,
      scenes: this.scenes,
      loadedScenes: this.loadedScenes,
      loadScene: (i, a) => this.loadScene(i, a),
      setCurrentSceneId: (id) => {
        this.currentSceneId = id
      },
      saveCurrentState: () => this.saveCurrentState(),
      evaluateCondition: (c) => this.evaluateChoiceCondition(c),
    })
    // Keep isRestoringState true until loadNext cascades (setTimeout 100ms per scene) have settled
    setTimeout(() => {
      this.isRestoringState = false
    }, 2000)
  }

  scrollToScene(sceneIndex: number): void {
    scrollToSceneFn(sceneIndex, { container: this.container, scenes: this.scenes, loadedScenes: this.loadedScenes })
  }

  setFlag(flag: string, value: boolean = true): void {
    this.flags[flag] = value
  }

  getFlag(flag: string): boolean {
    return this.flags[flag] || false
  }

  getCurrentSceneId(): string | null {
    return this.currentSceneId
  }

  // --- Choice restore / conditions ---

  private shouldLoadChoiceScene(sceneId: string, savedChoices: Record<string, number>): boolean {
    return shouldLoadChoiceSceneFn(sceneId, this.scenes, savedChoices, (result, choices) =>
      this.resolveChoiceResultForRestore(result, choices),
    )
  }

  private resolveChoiceResultForRestore(
    result: string | { sceneId: string; when?: ChoiceCondition } | Array<{ sceneId: string; when?: ChoiceCondition }>,
    savedChoices: Record<string, number>,
  ): string | null {
    return resolveChoiceResultForRestoreFn(result, savedChoices, (c) => this.evaluateChoiceCondition(c))
  }

  private evaluateChoiceCondition(condition: ChoiceCondition): boolean {
    return evaluateChoiceConditionFn(condition, {
      flags: this.flags,
      getSavedState: () => SaveStateManager.load(),
    })
  }

  // --- Preload ---

  setAudioPreloadConfig(config: AudioPreloadConfig): void {
    this.audioPreloadConfig = config
  }

  private getPreloadContext(): PreloadContext {
    return {
      scenes: this.scenes,
      hasLoaded: (id) => this.loadedScenes.has(id),
      hasPreloaded: (id) => this.preloadedScenes.has(id),
      markPreloaded: (id) => this.preloadedScenes.add(id),
      evaluateCondition: (c) => this.evaluateChoiceCondition(c),
      audioConfig: this.audioPreloadConfig,
    }
  }

  private triggerPreload(fromIndex: number): void {
    triggerPreloadAhead(fromIndex, this.getPreloadContext())
  }

  preloadInitialScenes(): void {
    doPreloadInitialScenes(this.getPreloadContext())
  }
}
